import { createSelector } from 'reselect';
import { getValueFromQueryParam, clone } from '@xto10x/core/lib/app.utils';
import appUtils, { getSelectedTeam, isUnboundedCycle } from 'utils/app.utils';
import { QueryParam } from 'utils/constants';
import analytics from '@xto10x/core/lib/analytics';
import {
  AnalyticsCategory,
  getFilterActionString,
  getFilterLabelString
} from 'utils/analytics.constants';
import { actions } from './actions';
import { actions as appActions } from '../../App/store/actions';

const initialState = {
  objectiveCycles: [],
  selectedObjectiveCycle: null,
  customRange: {
    start_date: null,
    end_date: null
  },
  selectedTeam: getSelectedTeam(
    [],
    getValueFromQueryParam(QueryParam.SELECTED_TEAM)
  ),
  notifications: [],
  notification_status: 0, // this flag means notification data is not yet fetched
  tasks: null,
  filters: null,
  dashboardFilters: null,
  metadata: null,
  unReadNotificationCount: 0,
  notificationLoadingStatus: false,
  searchObjectiveParams: null,
  showCompanyObjectivesOnly: true,
  showIndependentObjectivesOnly: true,
  levelFilterEnabled: false,
  showProtectedObjectives: true,
  showCustomObjectives: true,
  selectedLevelFilterValue: 2,
  contributorFilter: false
};

// Memorized  Selector Function
export const getUnReadNotificationCountSelector = createSelector(
  state => state.headerReducer.unReadNotificationCount,
  unReadNotificationCount => unReadNotificationCount
);

// Memorized  Selector Function
export const getNotificationsSelector = createSelector(
  state => state.headerReducer.notifications,
  notifications => notifications
);

export const getCustomRangeSelector = createSelector(
  state => state.headerReducer.customRange,
  customRange => customRange
);

export const getNotificationStatusSelector = createSelector(
  state => state.headerReducer.notification_status,
  notification_status => notification_status
);

// Memorized  Selector Function
export const getObjectiveCyclesSelector = createSelector(
  state => state.headerReducer.objectiveCycles,
  objectiveCycles => objectiveCycles
);

// Memorized  Selector Function
export const getSelectedObjectiveCycleSelector = createSelector(
  state => state.headerReducer.selectedObjectiveCycle,
  selectedObjectiveCycle => selectedObjectiveCycle
);

// Memorized  Selector Function
export const getSelectedTeamSelector = createSelector(
  state => state.headerReducer.selectedTeam,
  selectedTeam => selectedTeam
);

// Memorized  Selector Function
export const getSelectedTeamIdSelector = createSelector(
  state => state.headerReducer.selectedTeam,
  selectedTeam => selectedTeam.tag_id
);

export const getFilterParamsSelector = createSelector(
  state => state.headerReducer.filters,
  filters => filters
);

export const getDashboardFilterSelector = createSelector(
  state => state.headerReducer.dashboardFilters,
  dashboardFilters => dashboardFilters
);

export const getAttachments = createSelector(
  state => state.headerReducer.attachments,
  attachments => attachments
);

export const getDefaultActiveCycleSelector = createSelector(
  state => state.headerReducer.defaultActiveCycle,
  defaultActiveCycle => defaultActiveCycle
);

export const getUserTasksSelector = createSelector(
  state => state.headerReducer.tasks,
  tasks => tasks
);

export const getSearchObjectiveParams = createSelector(
  state => state.headerReducer.searchObjectiveParams,
  searchObjectiveParams => searchObjectiveParams
);

export const showCompanyObjectiveSelector = createSelector(
  state => state.headerReducer.showCompanyObjectivesOnly,
  showCompanyObjectivesOnly => showCompanyObjectivesOnly
);

export const showIndependentObjectiveSelector = createSelector(
  state => state.headerReducer.showIndependentObjectivesOnly,
  showIndependentObjectivesOnly => showIndependentObjectivesOnly
);

export const levelFilterToggleValueSelector = createSelector(
  state => state.headerReducer.levelFilterEnabled,
  levelFilterEnabled => levelFilterEnabled
);

export const getShowProtectedObjectivesSelector = createSelector(
  state => state.headerReducer.showProtectedObjectives,
  showProtectedObjectives => showProtectedObjectives
);

export const getShowCustomObjectivesSelector = createSelector(
  state => state.headerReducer.showCustomObjectives,
  showCustomObjectives => showCustomObjectives
);

export const levelFilterValueSelector = createSelector(
  state => state.headerReducer.selectedLevelFilterValue,
  selectedLevelFilterValue => selectedLevelFilterValue
);

export const getNextReverseOffsetSelector = createSelector(
  state => state.headerReducer.nextReverseOffset,
  nextReverseOffset => nextReverseOffset
);

export const getContributorFilterSelector = createSelector(
  state => state.headerReducer.contributorFilter,
  contributorFilter => contributorFilter
);

const HeaderReducer = (state = initialState, payload = {}) => {
  switch (payload.type) {
    case actions.SAVE_OBJECTIVE_CYCLE_LIST:
      return {
        ...state,
        objectiveCycles: payload.data,
        defaultActiveCycle: payload.data
          .filter(c => !isUnboundedCycle(c.cycle_type))
          .find(cycle => cycle.status !== 'EXPIRED')
      };

    case actions.SAVE_SELECTED_OBJECTIVE_CYCLE:
      return {
        ...state,
        selectedObjectiveCycle: payload.data,
        customRange: null
      };

    case actions.SAVE_SELECTED_CYCLE_RANGE:
      return {
        ...state,
        selectedObjectiveCycle: null,
        customRange: payload.data
      };

    case actions.SAVE_SELECTED_TEAM:
      return {
        ...state,
        selectedTeam: payload.data,
        dashboardFilters: null,
        searchObjectiveParams: null
      };

    case actions.SAVE_SINGLE_NOTIFICATION: {
      const { notification } = payload;
      const is_read = notification.read;
      return {
        ...state,
        notifications: [notification, ...state.notifications],
        previousOffSet: (state.previousOffSet ?? 0) + 1,
        nextReverseOffset: (state.nextReverseOffset ?? 0) + 1,
        unReadNotificationCount: !is_read
          ? state.unReadNotificationCount + 1
          : state.unReadNotificationCount,
        notificationLoadingStatus: false
      };
    }

    case actions.SAVE_NOTIFICATIONS: {
      const { notifications } = payload.notifications;
      const { metadata } = payload.notifications;
      const { total_unread_messages } = payload.notifications.metadata;

      if (payload.isOtherAction) {
        return {
          ...state,
          notifications,
          metadata,
          unReadNotificationCount:
            total_unread_messages != null
              ? total_unread_messages
              : state.unReadNotificationCount,
          notificationLoadingStatus: false
        };
      }

      return {
        ...state,
        notifications: [...state.notifications, ...notifications],
        metadata,
        unReadNotificationCount:
          total_unread_messages != null
            ? total_unread_messages
            : state.unReadNotificationCount,
        notificationLoadingStatus: false
      };
    }

    case actions.SET_FILTERS:
      if (payload.filters)
        analytics.trackEvent(
          AnalyticsCategory.filter_applied,
          getFilterActionString(payload.filters),
          getFilterLabelString(payload.filters)
        );

      return {
        ...state,
        filters: payload.filters
      };

    case actions.SET_DASHBOARD_FILTERS:
      if (payload.filters)
        analytics.trackEvent(
          AnalyticsCategory.filter_applied,
          'set_dashboard_filters',
          getFilterLabelString(payload.filters).concat(
            getFilterActionString(payload.filters)
          )
        );

      return {
        ...state,
        dashboardFilters: payload.filters
      };

    case actions.CLEAR_FILTERS:
      return {
        ...state,
        filters: null
      };

    case actions.SET_NOTIFICATION_LOADING:
      return {
        ...state,
        notificationLoadingStatus: payload.status
      };

    case actions.SAVE_TASKS_ACTION:
      return {
        ...state,
        tasks: payload.tasks
      };

    case appActions.ENABLE_SEARCH_ACTION: {
      const preSearchFilters = clone(state.filters);
      return {
        ...state,
        filters: null,
        searchObjectiveParams: {
          searchActive: true,
          preSearchFilters,
          searchTerm: payload.search_term
        }
      };
    }

    case actions.UPDATE_SEARCH_QUERY_STRING_ACTION: {
      if (
        state.searchObjectiveParams &&
        state.searchObjectiveParams.searchActive
      ) {
        return {
          ...state,
          searchObjectiveParams: {
            ...state.searchObjectiveParams,
            searchTerm: payload.search_term
          }
        };
      }
      return {
        ...state
      };
    }

    case appActions.RESET_SEARCH_STATE_ACTION: {
      return {
        ...state,
        searchObjectiveParams: null
      };
    }
    case actions.SET_SHOW_COMPANY_OBJECTIVES:
      return {
        ...state,
        showCompanyObjectivesOnly: payload.showCompanyObjective
      };

    case actions.SET_SHOW_INDEPENDENT_OBJECTIVES:
      return {
        ...state,
        showIndependentObjectivesOnly: payload.toggleState
      };

    case actions.SET_LEVEL_FILTER_TOGGLE_VALUE:
      return {
        ...state,
        levelFilterEnabled: payload.status
      };

    case actions.TOGGLE_PROTECTED_OBJECTIVES:
      return {
        ...state,
        showProtectedObjectives: payload.status
      };

    case actions.TOGGLE_CUSTOM_OBJECTIVES:
      return {
        ...state,
        showCustomObjectives: payload.status
      };

    case actions.SET_LEVEL_FILTER_VALUE:
      return {
        ...state,
        selectedLevelFilterValue: payload.value
      };

    case appActions.RESET_REDUCER:
      return {
        ...state,
        objectiveCycles: [],
        selectedObjectiveCycle: null,
        selectedTeam: {},
        notifications: [],
        filters: null,
        nextReverseOffset: null,
        previousOffSet: null,
        unReadNotificationCount: 0,
        notificationLoadingStatus: false,
        defaultActiveCycle: null,
        attachments: [],
        searchObjectiveParams: null
      };

    case actions.MARK_NOTIFICATION_AS_READ: {
      const notifications = appUtils.markNotificationAsRead(
        state.notifications,
        payload.notificationId
      );
      return {
        ...state,
        notifications
      };
    }

    case actions.SET_TOGGLE_CONTRIBUTOR_FILTER:
      return {
        ...state,
        contributorFilter: payload.value
      };

    default:
      return {
        ...state
      };
  }
};

export default HeaderReducer;
